import type { JSX } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { loginPathFor } from "../auth/returnTo";
import { useAuth } from "../auth/AuthProvider";
import { useLicensing } from "../auth/LicensingProvider";
import { LicensingModuleKeys } from "../types/licensing.types";
import { AllActions, AllModules } from "../accessManagement/AccessValues";
import { ModuleDisabled } from "./ModuleDisabled";
import { SubscriptionExpired } from "./SubscriptionExpired";
import { NotAuthorized } from "./NotAuthorized";

interface ProtectedRouteProps {
  children: JSX.Element;
  module?: any;
  action?: any;
  /** Narrower than the Org Admin bypass below — for a route that must stay
   * unreachable to an Org Admin (e.g. Role Management, Manage Providers),
   * not just hidden from their nav. Checked ahead of that bypass. */
  superAdminOnly?: boolean;
  /** For a route with no natural permission module of its own (e.g. an
   * admin action page, not a CRUD list backed by Module/Action rows) that
   * must still stay reachable to Org Admins as well as Super Admins.
   * Without this, omitting `module`/`action` would let ANY authenticated
   * user through at step 6 below — this closes that gap explicitly rather
   * than borrowing an unrelated module's permission as a stand-in gate. */
  adminOnly?: boolean;
  /** The licensing module this route belongs to when it is not one of the
   * three core modules — the template libraries, say. When the plan leaves
   * that module out, the route shows Module Disabled. */
  planModule?: string;
}

// Maps a route's `module` prop to the pitch shown on the Module Disabled
// page — separate from (and checked in addition to) the user-level
// permission check below, since a user can hold a permission for a module
// their organisation's plan does not include.
const ORG_MODULE_KEY: Record<number, "contract" | "obligation" | "receivable"> =
  {
    [AllModules.Contract]: "contract",
    [AllModules.Obligation]: "obligation",
    [AllModules.Receivable]: "receivable",
  };

// The plan-level module behind each permission module — what decides
// access.
const PLAN_MODULE_KEY: Record<number, string> = {
  [AllModules.Contract]: LicensingModuleKeys.ContractStudio,
  [AllModules.Obligation]: LicensingModuleKeys.ObligationHub,
  [AllModules.Receivable]: LicensingModuleKeys.ReceivablesEngine,
};

// Which pitch the Module Disabled page shows for a licensing module.
const PITCH_FOR_PLAN_MODULE: Record<
  string,
  "contract" | "obligation" | "receivable" | undefined
> = {
  [LicensingModuleKeys.ContractStudio]: "contract",
  [LicensingModuleKeys.ObligationHub]: "obligation",
  [LicensingModuleKeys.ReceivablesEngine]: "receivable",
};

export function ProtectedRoute({
  children,
  module,
  action,
  superAdminOnly,
  adminOnly,
  planModule,
}: ProtectedRouteProps) {
  const { user, loading, isAuthenticated, hasPermission } = useAuth();
  const { snapshot: licence } = useLicensing();
  const { pathname, search } = useLocation();

  /**
   * 1. Loading State
   */
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        Loading...
      </div>
    );
  }

  /**
   * 2. Authentication Check
   */
  if (!isAuthenticated || !user) {
    // Keep the address, so login can bring the person back to it.
    return <Navigate to={loginPathFor(pathname + search)} replace />;
  }

  /**
   * 3. Organisation Entitlement Check (Module Disabled Render)
   * Distinct from the user-level permission check below — this gates on
   * whether the organisation itself has the module enabled at all,
   * regardless of what the user is individually permitted to do. Checked
   * ahead of (and not exempted by) either admin bypass below: nobody,
   * including the org's own admin or the cross-org Super Admin, can reach a
   * module the organisation hasn't purchased/enabled — this is an
   * entitlement gate, not a permission one. enabledModules is null until
   * it's loaded, so this doesn't block access before we actually know one
   * way or the other.
   */
  // The subscription is the only source of truth for module access. An
  // organisation with no plan and no default plan is unrestricted, so this
  // gate opens for it; anything else follows the plan's module list. The
  // per-organisation switches that used to stand in for a plan are gone.
  const orgModuleKey = ORG_MODULE_KEY[module];
  const planModuleKey = PLAN_MODULE_KEY[module];
  const onPlan = !!licence && !licence.isUnrestricted;
  if (
    orgModuleKey &&
    onPlan &&
    planModuleKey &&
    licence.modules?.[planModuleKey] === false
  ) {
    return (
      <ModuleDisabled moduleKey={orgModuleKey} planModuleKey={planModuleKey} />
    );
  }
  if (planModule && onPlan && licence.modules?.[planModule] === false) {
    return (
      <ModuleDisabled
        moduleKey={PITCH_FOR_PLAN_MODULE[planModule]}
        planModuleKey={planModule}
      />
    );
  }

  /**
   * 3b. Subscription blocked (expired / suspended / cancelled): every
   * form that creates or changes data is replaced by the expired page.
   * Matched by the route's declared write action where it has one, and
   * by the URL shape otherwise, since not every form route declares one.
   * Reading stays open — lists, detail pages and the subscription page
   * itself — so an admin can still get to Recharge / Upgrade.
   */
  const isWriteAction =
    action === AllActions.CREATE ||
    action === AllActions.EDIT ||
    action === AllActions.UPLOADMEDIA;
  const looksLikeForm = /\/(new|edit|bulk|bulk-legacy)(\/|$)/.test(pathname);
  if (licence?.isBlocked && (isWriteAction || looksLikeForm)) {
    return <SubscriptionExpired reason={licence.blockedReason} />;
  }

  /**
   * 4. True Super Admin Only (checked ahead of the Org Admin bypass below —
   * an Org Admin must not reach this route even though isAdmin is true for
   * them too)
   */
  if (superAdminOnly && !user.isAppAdmin) {
    return <NotAuthorized />;
  }

  /**
   * 5. Admin-Only Action Pages (no backing permission module — see the
   * `adminOnly` doc comment above for why this can't just be "omit module")
   */
  if (adminOnly && !user.isAdmin) {
    return <NotAuthorized />;
  }

  /**
   * 6. Org Admin / Super Admin Bypass
   */
  if (user.isAdmin) {
    return children;
  }

  /**
   * 7. Permission Validation (Module Disabled Render)
   */
  // A route that names a module but no action is a read screen: it needs
  // Search. Before this, omitting the action (or naming one that does not
  // exist in AllActions) skipped the check entirely and let anyone through.
  if (module && !hasPermission(module, action ?? AllActions.SEARCH)) {
    return <NotAuthorized />;
  }

  /**
   * 8. Access Granted
   */
  return children;
}
